import { InputField } from "@/components/utils/InputField";
import { Colors } from "@/enums/Colors";
import { LazoPaths } from "@/enums/LazoPaths";
import FunderListService from "@/services/FunderListService";
import { Funder, FunderType } from "@/types/FunderTypes";
import { GeneratedNameOrigin } from "@/types/GenerateSmartlistNameDTO";
import { Box, Button, Typography, styled } from "@mui/material";
import { ChangeEvent, MutableRefObject, ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import CloseSVG from "../../../../assets/images/close-icon.svg";
import SaveToListButton from "../detail-view/SaveToListButton";

interface Props {
  loading: boolean;
  type: FunderType;
  selectedItemsCount?: number;
  resetSelectedItems: () => void;
  children?: ReactNode;
  selectedItems?: string[];
  smartListExternalId?: string;
  onChangeNameSearch(e: ChangeEvent<HTMLInputElement>): void;
  onClearNameFilter: () => void;
  nameRef: MutableRefObject<HTMLInputElement>;
  getMappedFilters: () => Object;
}

export default function AdvancedSearchFilters(props: Props) {
  const { t } = useTranslation("fundraising");
  const navigate = useNavigate();
  const [loadingCreation, setLoadingCreation] = useState<boolean>(false);

  const getSelectedFunders = (): Funder[] => {
    const type = props.type;

    return props.selectedItems?.map((f) => {
      return { uuid: f, type: type } as Funder;
    });
  };

  const handleCreateListWithFunders = (smartListName: string) => {
    const type = props.type;

    const selectedFunders = props.selectedItems?.map((f) => {
      return { uuid: f, type: type } as Funder;
    });

    if (selectedFunders.length == 0) return;
    setLoadingCreation(true);

    FunderListService.createListWithFunders(selectedFunders, smartListName || "")
      .then((res) => {
        res.onSuccess((newFunderList) => {
          if (!newFunderList.externalId) {
            return;
          }
          navigate(LazoPaths.FUNDER_LIST_DETAIL.replace(":externalId", newFunderList.externalId));
        });
        res.notifyErrorIfPresent();
      })
      .finally(() => setLoadingCreation(false));
  };

  const nameSearch = props.nameRef?.current?.value || "";

  return (
    <Container className="root-container">
      <Container className="selected-item-container">
        <StyledInputField
          hasValue={nameSearch !== ""}
          InputProps={{
            endAdornment: (
              <>
                {nameSearch?.trim() !== "" && (
                  <img className="close-icon-search" src={CloseSVG.src} alt="close-icon" onClick={props.onClearNameFilter} />
                )}
              </>
            ),
          }}
          customRef={props.nameRef}
          loading={props.loading}
          onChange={props.onChangeNameSearch}
          color={Colors.LAZO_PURPLE}
          autoComplete="off"
          placeHolder={t("advancedSearchPage.searchFilters.searchPlaceholder")}
          sx={{
            width: "14.5625rem",
          }}
        />

        {props.selectedItemsCount > 0 && (
          <>
            <SaveToListButton
              getFunders={getSelectedFunders}
              hideAlreadyInLists
              origin={GeneratedNameOrigin.ADVANCED_SEARCH}
              getMappedFilters={props.getMappedFilters}
              loading={loadingCreation}
              onCreateNewFunderList={handleCreateListWithFunders}
            />
            <Typography>
              {props.selectedItemsCount} {t("advancedSearchPage.searchFilters.resultsSelected")}
            </Typography>
            <Divider />

            <ClearButton onClick={props.resetSelectedItems}>{t("advancedSearchPage.searchFilters.clear")}</ClearButton>
          </>
        )}
      </Container>

      <Container className="filter-container">
        <Typography fontWeight={600}>{t("advancedSearchPage.searchFilters.filter")}</Typography>
        {props.children}
        {/*
        <Typography fontSize={"0.75rem"}>
          {props.resultsCount || 0} results
        </Typography>
        */}
      </Container>
    </Container>
  );
}

export const StyledInputField = styled(InputField, { shouldForwardProp: (prop) => prop !== "hasValue" })<{ hasValue?: boolean }>(
  ({ hasValue }) => ({
    "& .MuiOutlinedInput-notchedOutline": {
      border: hasValue ? `1px solid ${Colors.LAZO_PURPLE} !important` : "1px solid inherit",
      transition: "border 200ms",
    },

    "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
      border: `1px solid ${Colors.LAZO_PURPLE} !important`,
    },
  })
);

const Container = styled(Box)(() => ({
  "&.root-container": {
    padding: "1rem 2rem 1rem 1rem",
    display: "flex",
    justifyContent: "space-between",
  },
  "&.filter-container": {
    display: "flex",
    alignItems: "center",
    columnGap: "8px",
  },
  "&.selected-item-container": {
    display: "flex",
    alignItems: "center",
    columnGap: "1rem",
  },
  "& img.close-icon-search": {
    "&:hover": { cursor: "pointer" },
  },
}));

const ClearButton = styled(Button)(() => ({
  textTransform: "none",
  padding: "0px",
  width: "fit-content",
  minWidth: "fit-content",
  color: Colors.LAZO_PURPLE,
}));

const Divider = styled(Box)(() => ({
  width: "1px",
  height: "14px",
  backgroundColor: Colors.LIGHT_GRAY_DEDEE1,
}));
